import React, { useEffect, useState } from 'react';

const listeners = new Set();

// Fire a toast. If `sessionKey` is set, the toast is suppressed when the
// same key has already been shown in this browser session — used for the
// one-time-per-session deprecation toast on /companies and /customers
// redirects (PRD-006 F4 / AC-3).
export function showToast({ headline, body, sessionKey }) {
  if (sessionKey) {
    const flagKey = `cowork:legacyRedirectShown:${sessionKey}`;
    if (typeof window !== 'undefined' && window.sessionStorage.getItem(flagKey)) {
      return;
    }
    if (typeof window !== 'undefined') {
      window.sessionStorage.setItem(flagKey, '1');
    }
  }
  const toast = { headline, body, id: Date.now() + Math.random() };
  listeners.forEach((l) => l(toast));
}

export default function ToastContainer() {
  const [toast, setToast] = useState(null);

  useEffect(() => {
    const handler = (t) => setToast(t);
    listeners.add(handler);
    return () => listeners.delete(handler);
  }, []);

  useEffect(() => {
    if (!toast) return undefined;
    const timer = setTimeout(() => setToast(null), 6000);
    return () => clearTimeout(timer);
  }, [toast]);

  if (!toast) return null;

  return (
    <div
      className="fixed top-4 left-1/2 -translate-x-1/2 z-[100] max-w-xl w-[calc(100%-2rem)]"
      role="status"
      aria-live="polite"
      data-testid="legacy-deprecation-toast"
    >
      <div className="bg-slate-700 text-white rounded-lg shadow-lg px-4 py-3 flex items-start">
        <svg className="w-5 h-5 mt-0.5 mr-3 flex-shrink-0 text-slate-200" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
        </svg>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm">{toast.headline}</p>
          {toast.body && <p className="text-sm text-slate-200 mt-0.5">{toast.body}</p>}
        </div>
        <button
          onClick={() => setToast(null)}
          className="ml-3 p-1 text-slate-300 hover:text-white"
          aria-label="Dismiss"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  );
}
